import React, { useEffect } from 'react';
import { Card } from 'antd';
import { FieldArray, useFormikContext } from 'formik';
import { Input } from 'formik-antd';
import { useTranslation } from 'react-i18next';
import { FaPlusCircle, FaRegTimesCircle } from 'react-icons/fa';
import FormControl from '~/components/Form/FormControl';
import { SelectAntdFormik } from '~/components/Pagination/styles';
import InputCurrency from '~/components/Form/InputCurrency';
import Row from '~/components/Row';
import Button from '~/components/Button';
import { formatDecimal, parseCommaToDot, parsePrice } from '~/Utils/format';

const CARD_TYPES = {
  DEBIT: '0',
  CREDIT: '1',
  PIX: '2',
};

export default function RevenuesSection({ cardsOperators, fluidPermissions, handleTitle }) {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();

  const disabledCardSelect = (revenue) => {
    if (revenue?.id) return !revenue.card_operator_id;
    return !(revenue?.cardOptions?.length > 0);
  };

  const calculateRevenueRow = (index, rawPrice, cardType, fee) => {
    const raw = parsePrice(rawPrice || '0');

    if (raw <= 0 || (!cardType && cardType !== 0)) {
      setFieldValue(`revenues.${index}.fee_value`, '0,00');
      setFieldValue(`revenues.${index}.final_price`, '0,00');
      setFieldValue(`revenues.${index}.agio`, '0,00');

      return;
    }

    const machineFeePercent = parseCommaToDot(String(fee || '0'));
    const machineFeeAmount = raw * (machineFeePercent / 100);

    setFieldValue(`revenues.${index}.fee_value`, parsePrice(machineFeeAmount));
    setFieldValue(`revenues.${index}.final_price`, parsePrice(raw - machineFeeAmount));

    let correctFee = 0;

    if (cardType == CARD_TYPES.DEBIT) correctFee = parseCommaToDot(String(values.debit_fee || '0'));
    else if (cardType == CARD_TYPES.CREDIT) correctFee = parseCommaToDot(String(values.credit_fee || '0'));
    else if (cardType == CARD_TYPES.PIX) correctFee = parseCommaToDot(String(values.pix_fee || '0'));

    const customerFeeAmount = raw * (correctFee / 100);
    const agio = customerFeeAmount - machineFeeAmount;

    setFieldValue(`revenues.${index}.agio`, formatDecimal(agio));
  };

  const handleCardTypeSelect = (value, option, index, revenue) => {
    const selectedCard = revenue?.cardOptions?.find((c) => c.id == option.key);

    setFieldValue(`revenues.${index}.fee`, selectedCard?.fee);
    setFieldValue(`revenues.${index}.card_id`, option.key);
    setFieldValue(`revenues.${index}.card_type`, value);

    calculateRevenueRow(index, revenue.raw_price, value, selectedCard?.fee);
  };

  useEffect(() => {
    if (cardsOperators?.length > 0 && values?.revenues?.length > 0) {
      values.revenues.forEach((revenue, index) => {
        if (!revenue.cardOptions || revenue.cardOptions.length === 0) {
          const cardOperator = cardsOperators.find((co) => co.id === revenue.card_operator_id);
          if (cardOperator) {
            setFieldValue(`revenues.${index}.cardOptions`, cardOperator.cards);
          }
        }
      });
    }
  }, [values?.revenues]);

  useEffect(() => {
    if (values?.revenues?.length > 0) {
      values.revenues.forEach((revenue, index) => {
        if (revenue.raw_price && revenue.card_type !== null && revenue.card_type !== undefined) {
          calculateRevenueRow(index, revenue.raw_price, revenue.card_type, revenue.fee);
        }
      });
    }
  }, [values.debit_fee, values.credit_fee, values.pix_fee]);

  return (
    <FieldArray
      name="revenues"
      render={(arrayHelpers) => (
        <>
          {values?.revenues?.map((revenue, index) => (
            <Card
              key={'revenue' + index}
              style={{ marginBottom: 16 }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{handleTitle(values, index, revenue?.cardOptions)}</span>
                  <Button disabled={!fluidPermissions()} danger type="text" onClick={() => arrayHelpers.remove(index)}>
                    <FaRegTimesCircle size={18} />
                  </Button>
                </div>
              }
            >
              <Row>
                <FormControl cols={{ sm: 12, xs: 24 }} label={t('Cartão')} field={`revenues.${index}.card_operator_id`}>
                  <SelectAntdFormik
                    placeholder="Selecione um cartão"
                    name={`revenues.${index}.card_operator_id`}
                    onSelect={(value) => {
                      const cardOperator = cardsOperators.find((co) => co.id === value);
                      setFieldValue(`revenues.${index}.cardOptions`, cardOperator?.cards || []);
                      setFieldValue(`revenues.${index}.card_type`, null);
                      setFieldValue(`revenues.${index}.raw_price`, null);
                      setFieldValue(`revenues.${index}.fee`, null);
                      setFieldValue(`revenues.${index}.agio`, null);
                    }}
                    disabled={!fluidPermissions()}
                  >
                    {cardsOperators.map((operator) => (
                      <SelectAntdFormik.Option key={operator.id} value={operator.id}>
                        {operator.name}
                      </SelectAntdFormik.Option>
                    ))}
                  </SelectAntdFormik>
                </FormControl>
                <FormControl cols={{ sm: 12, xs: 24 }} label={t('Modalidade')} field={`revenues.${index}.card_type`}>
                  <SelectAntdFormik
                    placeholder="Selecione uma modalidade"
                    name={`revenues.${index}.card_type`}
                    onSelect={(value, option) => handleCardTypeSelect(value, option, index, revenue)}
                    disabled={disabledCardSelect(revenue) || !fluidPermissions()}
                  >
                    {revenue?.cardOptions?.map((card) => (
                      <SelectAntdFormik.Option key={card.id} value={card.card_type}>
                        {card.card_type_name}
                      </SelectAntdFormik.Option>
                    ))}
                  </SelectAntdFormik>
                </FormControl>
              </Row>
              <Row>
                <FormControl cols={{ sm: 12, xs: 24 }} label={t('Valor bruto')} field={`revenues.${index}.raw_price`}>
                  <InputCurrency
                    name={`revenues.${index}.raw_price`}
                    disabled={!fluidPermissions()}
                    onBlur={(e) => {
                      calculateRevenueRow(index, e.target.value, revenue.card_type, revenue.fee);
                    }}
                  />
                </FormControl>
                <FormControl cols={{ sm: 12, xs: 24 }} label={t('Taxa real (%)')} field={`revenues.${index}.fee`}>
                  <Input
                    name={`revenues.${index}.fee`}
                    disabled={!fluidPermissions()}
                    onBlur={(e) => {
                      calculateRevenueRow(index, revenue.raw_price, revenue.card_type, e.target.value);
                    }}
                  />
                </FormControl>
              </Row>
              <Row>
                <FormControl cols={{ sm: 8, xs: 24 }} label={t('Taxa')} field={`revenues.${index}.fee_value`}>
                  <InputCurrency name={`revenues.${index}.fee_value`} disabled />
                </FormControl>
                <FormControl
                  cols={{ sm: 8, xs: 24 }}
                  label={t('Valor líquido')}
                  field={`revenues.${index}.final_price`}
                >
                  <InputCurrency name={`revenues.${index}.final_price`} disabled />
                </FormControl>
                <FormControl
                  cols={{ sm: 8, xs: 24 }}
                  label={t('Ágio de taxa administrativa')}
                  field={`revenues.${index}.agio`}
                >
                  <InputCurrency name={`revenues.${index}.agio`} disabled />
                </FormControl>
              </Row>
            </Card>
          ))}
          <Button
            type="primary"
            icon={<FaPlusCircle />}
            onClick={() =>
              arrayHelpers.push({
                card_id: null,
                card_type: null,
                card_operator_id: null,
                raw_price: '0.0',
                fee: 0.0,
                fee_value: '0.0',
                final_price: '0.0',
                agio: '0.0',
                cardOptions: [],
              })
            }
            style={{ marginTop: 16 }}
            disabled={!fluidPermissions()}
          >
            {t('Adicionar cartão')}
          </Button>
        </>
      )}
    />
  );
}
